import { insurance } from '@getpopsure/private-constants';
import {
  ActiveTariff,
  EmploymentStatus,
  getPriceBreakdown,
} from '@getpopsure/private-health-insurance-pricing-engine';

const SHORT_TERM_PLANS: ShortTermPlan[] = ['HiMedicalPlus', 'HiMedical'];

type ExtractStrict<T, U extends T> = Extract<T, U>;

export type ShortTermPlan = ExtractStrict<
  ActiveTariff,
  'HiMedical' | 'HiMedicalPlus'
>;

export type LongTermPlan = Exclude<ActiveTariff, ShortTermPlan>;

export const isShortTermPlan = (plan?: ActiveTariff): plan is ShortTermPlan =>
  !!plan && SHORT_TERM_PLANS.includes(plan as ShortTermPlan);

export const planConversionMapping: Record<ActiveTariff, ActiveTariff> = {
  NKSelectS: 'HiMedical',
  NKSelectL: 'HiMedicalPlus',
  NKSelectXL: 'HiMedicalPlus',
  HiMedical: 'NKSelectS',
  HiMedicalPlus: 'NKSelectXL',
};

export const PRICE_PLACEHOLDER_TEXT = '€—' as const;

export const EMPLOYED_INCOME_THRESHOLD =
  insurance.privateHealth.employeeThreshold;
export const GENERAL_NON_EMPLOYEE_INCOME_THRESHOLD = 25_000;
export const HALLESCHE_FREELANCER_INCOME_THRESHOLD = 30_000;
export const HALLESCHE_OTHER_INCOME_THRESHOLD = 60_000;

export const YEARLY_MINIJOB_THRESHOLD =
  insurance.healthInsurance.miniJobThreshold * 12;
export const PRIVATE_HEALTH_INCOME_TRESHOLD =
  insurance.privateHealth.employeeThreshold;

export const HANSE_MERKUR_AGE_THRESHOLD = 35;
export const GENERAL_AGE_THRESHOLD = 90;
export const CHILD_AGE_THRESHOLD = 25;

export type PersonKey = 'MAIN' | 'SPOUSE' | `CHILD-${string}`;

export type FamilyInputDataMap = Map<PersonKey, CalculatorInputData>;

export type CalculationResultsTextData = {
  infoTitle?: string;
  infoDetails?: string;
};

export type CalculatorInputData = {
  birthYear?: number;
  income?: number;
  employmentStatus?: EmploymentStatus;
  plan?: ActiveTariff;
  hasEUPassport?: boolean;
  hasPermanentResidencyPermit?: boolean;
  isPlanningToStayMoreThan5Years?: boolean;
};

export type PriceBreakdown = ReturnType<typeof getPriceBreakdown>;
export type UnusedEmployerContributions =
  PriceBreakdown['contributions']['unusedEmployerContributions'];

export type GeneralContentVariant = 'NO_PRICE';

export type MainContentVariant =
  | 'EMPLOYED_IN_GERMANY--INCOME_TOO_LOW'
  | 'OTHER--HANSE_MERKUR_REDIRECT'
  | 'OTHER--INCOME_TOO_LOW'
  | 'OTHER--INCOME_TOO_LOW_BUT_TOO_OLD_FOR_HANSE_MERKUR'
  | 'SELF_EMPLOYED--HANSE_MERKUR_REDIRECT'
  | 'SELF_EMPLOYED--INCOME_TOO_LOW'
  | 'SELF_EMPLOYED--INCOME_TOO_LOW_BUT_TOO_OLD_FOR_HANSE_MERKUR'
  | 'TOO_OLD';

export type SpouseContentVariant =
  | `SPOUSE--${MainContentVariant}`
  | 'SPOUSE--MAIN_NOT_ANSWERED'
  | 'SPOUSE--MAIN_NOT_ELIGIBLE'
  | 'SPOUSE--MAIN_ON_HANSE_MERKUR';

export type ChildContentVariant =
  | 'CHILD--TOO_OLD'
  | 'CHILD--MAIN_NOT_ANSWERED'
  | 'CHILD--MAIN_NOT_ELIGIBLE'
  | 'CHILD--MAIN_ON_HANSE_MERKUR';

export type ContentVariant =
  | GeneralContentVariant
  | MainContentVariant
  | SpouseContentVariant
  | ChildContentVariant;

export type CashbackData = NonNullable<
  ReturnType<typeof getPriceBreakdown>['cashback']
>;

type PlanString = `${string} plan`;

export type PriceData = {
  monthlyPrice: number;
  discountedMonthlyPrice?: number;
  unusedEmployerContributions?: {
    base: number;
    longTermCare: number;
  };
  isDependent?: boolean;
  cashbackData?: CashbackData;
  planStr: PlanString;
  publicPrice?: number;
};

export type CalculatorOutputData<C> =
  | {
      priceData?: undefined;
      contentVariant?: C;
    }
  | {
      priceData?: PriceData;
      contentVariant?: undefined;
    };

export const employmentStatusToHallescheThreshold: Record<
  EmploymentStatus,
  number
> = {
  EMPLOYED_IN_GERMANY: EMPLOYED_INCOME_THRESHOLD,
  EMPLOYED_OUTSIDE_OF_GERMANY: HALLESCHE_FREELANCER_INCOME_THRESHOLD,
  SELF_EMPLOYED: HALLESCHE_FREELANCER_INCOME_THRESHOLD,
  OTHER: HALLESCHE_OTHER_INCOME_THRESHOLD,
};

export type FamilyQuoteData = {
  name: string;
  price: number;
  planStr: PlanString;
};
